"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

export default function EditCategoryPage () {
    const { id } = useParams(); // Get category ID from URL
    const [formData, setFormData] = useState({
        title: "",
        image: "",
    });
    const [imagePreview, setImagePreview] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Fetch category data when component mounts
    useEffect(() => {
        if (id) {
            fetchCategoryData(id);
        } else {
            setIsLoading(false);
            toast.error("Category ID is missing");
            navigate("/dashboard/categories");
        }
    }, [id]);

    const fetchCategoryData = async (id) => {  // Accept ID as parameter
        try {
            setIsLoading(true);
            setError(null);

            // Fetch all categories
            const response = await fetch(`https://leapatinternational.com/admin/api/v1/category.php`);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            if (result.status === "success" && result.data) {
                // Find the category with matching ID
                const category = result.data.find(category => category.id === id);
                if (category) {
                    // Populate form with the matched category data
                    setFormData({
                        title: category.title || "",
                        image: category.image || "",
                    });
                    setImagePreview(category.image || "");
                } else {
                    throw new Error(`Category with ID ${id} not found`);
                }
            } else {
                throw new Error(result.message || "Failed to fetch categories");
            }
        } catch (err) {
            console.error("Error fetching category:", err);
            setError(err.message || "Failed to load category data");
            toast.error(err.message || "Failed to load category data. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Store the file object
            setFormData((prev) => ({
                ...prev,
                image: file,
            }));

            // Create preview URL
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Validate form
            if (!formData.title.trim()) {
                toast.error("Category title is required");
                setIsSubmitting(false);
                return;
            }

            const data = new FormData();
            data.append("id", id); // Add category ID for update
            data.append("title", formData.title);

            // Handle image - check if it's a File object or a string URL
            if (formData.image instanceof File) {
                data.append("image", formData.image);
            } else if (formData.image && typeof formData.image === "string") {
                // If it's an existing image URL, send it as image_url
                data.append("image_url", formData.image);
            }

            // Use update endpoint
            const endpoint = "https://leapatinternational.com/admin/api/v1/category_edit.php";

            const response = await fetch(endpoint, {
                method: "POST",
                body: data,
            });
            const result = await response.json();

            if (result.status === "success") {
                toast.success("Category updated successfully!");
                navigate("/dashboard/categories");
            } else {
                toast.error(result.message || "Failed to update category");
            }
        } catch (err) {
            console.error("Error:", err);
            toast.error("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate("/dashboard/categories");
    };

    const removeImage = () => {
        setFormData((prev) => ({
            ...prev,
            image: "",
        }));
        setImagePreview("");
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="edit-category-page">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3">Loading category data...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="edit-category-page">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="text-center py-5">
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                    <button className="btn btn-primary mt-3" onClick={() => navigate("/dashboard/categories")}>
                                        Back to Categories
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="category-management-section">
            {/* Page Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex align-items-center  justify-content-between mb-3">

                        <div>
                            <h1 className="page-title mb-0">Edit Category</h1>
                            <p className="page-subtitle mb-0">Update category information</p>
                        </div>
                        <button className="back-button me-3" onClick={handleCancel}>
                            ← Back to Categories
                        </button>

                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Category Information</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Category Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                placeholder="Enter category name"
                                                required
                                            />
                                            <div className="form-text">Choose a clear, descriptive name for your category</div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Category Image</label>
                                            <input type="file" className="form-control" accept="image/*" onChange={handleImageUpload} />
                                            <div className="form-text">Upload an image to represent this category (JPG, PNG, GIF)</div>

                                            {imagePreview && (
                                                <div className="mt-3">
                                                    <label className="form-label">Image Preview</label>
                                                    <div className="image-preview-container position-relative">
                                                        <img
                                                            src={imagePreview || "/placeholder.svg"}
                                                            alt="Category preview"
                                                            className="img-thumbnail"
                                                            style={{ width: "200px", height: "200px", objectFit: "cover" }}
                                                            onError={(e) => {
                                                                e.target.src = "/placeholder.svg";
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                                            onClick={removeImage}
                                                            title="Remove image"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex gap-2 pt-3 border-top">
                                    <button type="submit" className="add-button" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            <>Update Category</>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="back-button ps-3 pe-3"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

